import { useEffect, useId, useState } from 'react'

import Action from 'ageas-react-component-action'
import Dropdown from 'ageas-react-component-dropdownlist'
import MiniLoader from 'ageas-react-component-miniloader'
import Section from 'ageas-react-component-section'
import TextField from 'ageas-react-component-textfield'

import { Result } from '../../../@types/PolicyHolderDetails'
import { AddressDetails } from '../../../@types/ValidateZipCode'
import { useLoadPolicyHolderReferenceDataStore } from '../../../Stores/useLoadPolicyHolderReferenceDataStore'
import { useZipCodeStore } from '../../../Stores/useZipCodeStore'
import { useValidateZipCode } from '../../../hooks/api/useValidateZipCode'
import { t } from '../../../i18n'
import {
  streetNameFormatted,
  streetNameFormattedFromZipCode,
} from '../../../utils/streetNameFormatted'
import { Disclaimer } from '../../Disclaimer'
import { validateMandatoryFields } from '../CreateEntity'
import { SelectAddress } from './SelectAddress'

export interface FormAddressValuesProps {
  entityTypeArteria: string
  entityNameArteria: string
  entityNumberPolice: string
  entityFloor: string
  entityComplement: string
  entityNumberDoor: string
  entityZipCode: string
  entityLocalization: string
  entityPostalAddress: string
}

type Props = {
  isAnimal?: boolean
  hiddenTitle?: boolean
  onChange: (value: FormAddressValuesProps) => void
  onAddressSelected?: (value: AddressDetails) => void
  initialFormValues: FormAddressValuesProps
  isCreateEntity?: boolean
  onNextChange?: () => void
  onZipCodeSearched?: (value: boolean) => void
}

export const initialFormAddressValues = {
  entityTypeArteria: '',
  entityNameArteria: '',
  entityNumberPolice: '',
  entityComplement: '',
  entityFloor: '',
  entityNumberDoor: '',
  entityZipCode: '',
  entityLocalization: '',
  entityPostalAddress: '',
}

const fieldsMandatory = ['entityLocalization', 'entityZipCode']

export const AddressForm = ({
  isAnimal = false,
  hiddenTitle = false,
  onChange,
  onAddressSelected,
  initialFormValues,
  isCreateEntity = false,
  onNextChange,
  onZipCodeSearched,
}: Props) => {
  const id = useId()
  const [formValues, setFormValues] =
    useState<Partial<FormAddressValuesProps>>(initialFormValues)
  const { policyHolderReferenceData } = useLoadPolicyHolderReferenceDataStore()
  const { zipCodeResult } = useZipCodeStore()
  const [isStreetNamePristine, setIsStreetNamePristine] = useState(false)
  const [isStreetTypePristine, setIsStreetTypePristine] = useState(false)
  const [isZipCodePristine, setIsZipCodePristine] = useState(false)
  const [showAddressTable, setShowAddressTable] = useState(false)
  const [addressSelected, setAddressSelected] = useState<AddressDetails | null>(
    null,
  )

  const [showAddressContent, setShowAddressContent] = useState(
    (initialFormValues?.entityZipCode || '')?.length > 0,
  )

  const formatLovValues = (values: Result[]) => {
    return values.map((value) => ({
      caption: value?.description,
      value: value?.idElement,
    }))
  }

  const {
    data: zipCodeData,
    mutate: mutateZipCode,
    isLoading: isLoadingZipCode,
    isSuccess: isSuccessZipCode,
  } = useValidateZipCode()

  const handleSetAddress = (currentAddress: AddressDetails) => {
    setShowAddressContent(true)
    handleSetValuesAddress(currentAddress)
    setAddressSelected(currentAddress)
    onAddressSelected && onAddressSelected(currentAddress)
  }

  const handleAddressSelected = (addressSelected: AddressDetails) => {
    handleSetAddress(addressSelected)
    setShowAddressTable(false)
  }

  const handleFormValues = (value: any) => {
    setFormValues((prevState) => ({
      ...prevState,
      ...value,
    }))
  }

  const handleSetValuesAddress = (address: AddressDetails) => {
    handleFormValues({ entityPostalAddress: address?.zipCodeDescription || '' })
    handleFormValues({ entityLocalization: address?.town || '' })
    handleFormValues({
      entityNameArteria: streetNameFormatted(
        streetNameFormattedFromZipCode(address, true) || '',
        address?.streets?.[0]?.streetType || '',
      ),
    })
    handleFormValues({
      entityTypeArteria:
        policyHolderReferenceData?.streetsTypes?.find(
          (i) => i.description === address?.streets?.[0]?.streetType,
        )?.idElement || '',
    })
  }

  const handleSubmitZipCode = (zipCodeValue = formValues.entityZipCode) => {
    if ((zipCodeValue || '')?.length > 0) {
      mutateZipCode(zipCodeValue!)
      setFormValues({
        ...initialFormAddressValues,
        entityZipCode: zipCodeValue,
      })
      setShowAddressTable(false)
      setShowAddressContent(false)
    }
  }

  useEffect(() => {
    onZipCodeSearched && onZipCodeSearched(isZipCodePristine)
  }, [isZipCodePristine])

  const zipCodeMessage = () => {
    const zipCodeLength = (formValues?.entityZipCode || '')?.length
    if (zipCodeLength > 0) {
      if ((zipCodeData?.addresses || [])?.length === 0 && isSuccessZipCode) {
        return t('pages.step1.errors.invalidZipCode')
      }
    } else if (zipCodeLength === 0 && isZipCodePristine) {
      return t('pages.step1.errors.mandatoryField')
    }

    return ''
  }

  const streetTypeMessage = () => {
    const entityTypeArteriaLength = (formValues?.entityTypeArteria || '')
      ?.length

    if (entityTypeArteriaLength === 0 && isStreetTypePristine) {
      return t('pages.step1.errors.mandatoryField')
    }

    return ''
  }

  const streetNameMessage = () => {
    const entityNameArteriaLength = (formValues?.entityNameArteria || '')
      ?.length

    if (entityNameArteriaLength === 0 && isStreetNamePristine) {
      return t('pages.step1.errors.mandatoryField')
    }

    return ''
  }

  const handleChangeZipCode = (value: string) => {
    if (addressSelected !== null && addressSelected?.postalCode7 !== value) {
      handleSubmitZipCode(value)
    }
  }

  useEffect(() => {
    onChange(formValues as never)
  }, [formValues, onChange])

  useEffect(() => {
    if (isSuccessZipCode) {
      setIsZipCodePristine(false)

      if ((zipCodeResult?.addresses || [])?.length === 1) {
        handleSetAddress((zipCodeResult?.addresses || [])?.[0])
      } else if ((zipCodeResult?.addresses || [])?.length > 1) {
        setShowAddressTable(true)
      }
    }
  }, [isSuccessZipCode, zipCodeResult])

  return (
    <>
      {!hiddenTitle && (
        <Section
          id={`${id}createEntityAddressSection`}
          name={t('forms.addresses.sectionAddress')}
          config={{ labelSize: '20px', labelBold: '500' }}
          colorTheme="blue"
        />
      )}
      <TextField
        isSearch
        message={zipCodeMessage()}
        id={`${id}entityZipCode-${formValues.entityZipCode}`}
        value={formValues.entityZipCode}
        label={t('forms.addresses.zipCode')}
        colorTheme="blue"
        maxLength={10}
        mask="0000-000"
        version="2"
        validation="^\d{4}-\d{3}$"
        onValueChanged={(id: string, value: string) => {
          setIsZipCodePristine(true)
          handleFormValues({ entityZipCode: value })
          handleChangeZipCode(value)
        }}
        onClick={handleSubmitZipCode}
      />
      {isLoadingZipCode && (
        <MiniLoader color="white" minHeight="auto" maxWidth="auto" />
      )}
      {showAddressTable && (
        <SelectAddress
          data={zipCodeResult?.addresses || []}
          onSelectedAddress={handleAddressSelected}
        />
      )}
      {showAddressContent && (
        <>
          <TextField
            required
            readOnly
            id={`${id}entityPostalAddress-${formValues.entityPostalAddress}`}
            key={`${id}entityPostalAddress-${formValues.entityPostalAddress}`}
            name="entityPostalAddress"
            value={formValues.entityPostalAddress || ''}
            label={t('forms.addresses.postalAddress')}
            colorTheme="blue"
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityPostalAddress: value })
            }
          />
          <TextField
            required
            readOnly
            id={`${id}entityLocalization-${formValues.entityLocalization}`}
            key={`${id}entityLocalization-${formValues.entityLocalization}`}
            name="entityLocalization"
            value={formValues.entityLocalization || ''}
            label={t('forms.addresses.localization')}
            colorTheme="blue"
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityLocalization: value })
            }
          />
          <Dropdown
            message={streetTypeMessage()}
            id={`${id}entityTypeArteria-${formValues.entityTypeArteria}`}
            key={`${id}entityTypeArteria-${formValues.entityTypeArteria}`}
            name="entityTypeArteria"
            label={t('forms.addresses.typeArteria')}
            readOnly
            value={formValues.entityTypeArteria}
            onValueChanged={(id: string, value: string) => {
              setIsStreetTypePristine(true)
              handleFormValues({ entityTypeArteria: value })
            }}
            colorTheme="blue"
            options={formatLovValues(
              policyHolderReferenceData?.streetsTypes || [],
            )}
            version="2"
          />
          <TextField
            readOnly
            message={streetNameMessage()}
            id={`${id}entityNameArteria-${formValues?.entityNameArteria}`}
            key={`${id}entityNameArteria-${formValues?.entityNameArteria}`}
            name="entityNameArteria"
            label={t('forms.addresses.sectionAddress')}
            colorTheme="blue"
            maxLength={50}
            value={
              (formValues?.entityNameArteria || '')?.length === 0
                ? `${formValues.entityZipCode} ${formValues.entityPostalAddress}`
                : streetNameFormatted(
                    formValues?.entityNameArteria || '',
                    formValues?.entityTypeArteria || '',
                  )
            }
            version="2"
            onValueChanged={(id: string, value: string) => {
              setIsStreetNamePristine(true)
              handleFormValues({
                entityNameArteria: streetNameFormatted(
                  value || '',
                  formValues?.entityTypeArteria || '',
                ),
              })
            }}
          />
          <TextField
            optionalLabel
            id={`${id}entityNumberPolice-${formValues.entityNumberPolice}`}
            key={`${id}entityNumberPolice-${formValues.entityNumberPolice}`}
            name="entityNumberPolice"
            value={formValues.entityNumberPolice}
            label={t('forms.addresses.policeNumber')}
            colorTheme="blue"
            maxLength={10}
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityNumberPolice: value })
            }
          />
          <TextField
            optionalLabel
            id={`${id}entityFloor-${formValues.entityFloor}`}
            key={`${id}entityFloor-${formValues.entityFloor}`}
            name="entityFloor"
            value={formValues.entityFloor}
            label={t('forms.addresses.floor')}
            colorTheme="blue"
            maxLength={10}
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityFloor: value })
            }
          />
          <TextField
            optionalLabel
            id={`${id}entityNumberDoor-${formValues.entityNumberDoor}`}
            key={`${id}entityNumberDoor-${formValues.entityNumberDoor}`}
            name="entityNumberDoor"
            value={formValues.entityNumberDoor}
            label={t('forms.addresses.doorNumber')}
            colorTheme="blue"
            maxLength={10}
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityNumberDoor: value })
            }
          />
          <TextField
            optionalLabel
            id={`${id}entityComplement-${formValues.entityComplement}`}
            key={`${id}entityComplement-${formValues.entityComplement}`}
            name="entityComplement"
            label={t('forms.addresses.complement')}
            value={formValues.entityComplement}
            maxLength={25}
            colorTheme="blue"
            version="2"
            onValueChanged={(id: string, value: string) =>
              handleFormValues({ entityComplement: value })
            }
          />
          {isCreateEntity && (
            <>
              <Disclaimer text={t('addressSelectTable.disclaimerInfo')} />
              <Disclaimer text={t('addressSelectTable.disclaimerFont')} />
              <Action
                id="nextEntityCreateButton-1"
                label={t('pages.step3.createEntity.next')}
                type="button"
                disable={!validateMandatoryFields(formValues, fieldsMandatory)}
                onValueChanged={onNextChange}
              />
            </>
          )}
        </>
      )}
    </>
  )
}
